import { DatoDocumentType } from '@src/components/Document/PageTemplates/Category/Three/query';

/**
 * Quick link structure from GraphQL
 */
export interface QuickLinkItem {
  title: string;
  slug: string;
}

export interface QuickLinkSection {
  id?: string;
  title?: string;
  links: QuickLinkItem[];
  module?: {
    id: string;
    slug: string;
  };
}

export interface DocumentCategoryWithQuickLinks {
  originalId: string;
  id: string;
  name: string;
  slug: string;
  quickLinks?: QuickLinkSection[];
}

/**
 * Determines which quick links should be rendered:
 * 1) Module quick links take highest priority
 * 2) Document categories are checked from last to first element
 * 3) Returns the first quick links section found with actual content
 *
 * @param document The document data from GraphQL
 * @returns The quick links section to render, or null if none found
 */
export function getQuickLinksToRender(
  document: DatoDocumentType,
  module?: string,
): QuickLinkSection | null {
  // 1) Check module quick links first
  if (document.module?.quickLinks?.length) {
    const moduleQuickLinks = document.module.quickLinks.find(
      (section) => section.links && section.links.length > 0,
    );

    if (moduleQuickLinks) {
      return moduleQuickLinks;
    }
  }

  // 2) Check document categories from last to first
  if (document.documentCategory?.length) {
    // Cast to extended interface to access quickLinks
    const categoriesWithQuickLinks = document.documentCategory;

    // Iterate from last to first element
    for (let i = categoriesWithQuickLinks.length - 1; i >= 0; i--) {
      const category = categoriesWithQuickLinks[i];

      if (category.quickLinks?.length) {
        const categoryQuickLinks = category.quickLinks.find(
          (section) => section.links && section.links.length > 0,
        );

        const categoryQuickLinksByModule = category.quickLinks.find(
          (section) =>
            section.links && section.links.length > 0 && section?.module?.slug === module,
        );

        // If we find quick links that match the module, return those first
        if (categoryQuickLinksByModule) {
          return categoryQuickLinksByModule;
        }

        // Otherwise, return the first available quick links for the category
        if (categoryQuickLinks) {
          return categoryQuickLinks;
        }
      }
    }
  }

  // No suitable quick links found
  return null;
}

/**
 * Type guard to check if quick links should be rendered
 */
export function hasQuickLinks(quickLinks: QuickLinkSection | null): quickLinks is QuickLinkSection {
  return quickLinks !== null && quickLinks.links.length > 0;
}
